import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import PageContainer from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/lib/localization";
import { useAuth } from "@/lib/store";
import { fetchAnalyticsRestockPlanPage } from "@/lib/shopApi";
import { formatOperatingCurrency } from "@/lib/money";

const PAGE_LIMIT = 40;

const urgencyStyles = {
  high: {
    dot: "bg-destructive",
    pill: "border-destructive/20 bg-destructive/10 text-destructive",
  },
  medium: {
    dot: "bg-warning",
    pill: "border-warning/20 bg-warning/10 text-warning",
  },
  low: {
    dot: "bg-success",
    pill: "border-success/20 bg-success/10 text-success",
  },
};

function normalizeRestockPlan(response) {
  return response?.restockPlan ?? null;
}

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function formatGeneratedAt(value, locale, copy) {
  if (!value) return copy.reviewedNow;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return copy.reviewedNow;
  return copy.reviewedAt(
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date)
  );
}

export default function AnalyticsRestockList() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.restockList;
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const loadPlan = useCallback(async (nextOffset = 0) => {
    if (!session?.sessionId) {
      setError(messages.analytics.sessionNotReady);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetchAnalyticsRestockPlanPage(session.sessionId, {
        limit: PAGE_LIMIT,
        offset: nextOffset,
      });
      setPlan(normalizeRestockPlan(response));
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.message || copy.errorTitle);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [copy.errorTitle, messages.analytics.sessionNotReady, session?.sessionId]);

  useEffect(() => {
    void loadPlan(0);
  }, [loadPlan]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const recommendations = useMemo(() => plan?.recommendations ?? [], [plan?.recommendations]);
  const totalCount = plan?.totalCount ?? 0;
  const currentPageNumber = totalCount ? Math.floor((plan?.offset ?? 0) / PAGE_LIMIT) + 1 : 1;
  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_LIMIT)) : 1;

  return (
    <PageContainer className="px-4 py-6 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mb-3 h-8 px-2 text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/analytics/restock-plan")}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {messages.analytics.restockPlanner.title}
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">{copy.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {copy.subtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            disabled={loading}
            onClick={() => void loadPlan(offset)}
          >
            <RefreshCcw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {messages.analytics.refresh}
          </Button>
        </div>

        {loading ? (
          <div className="luxe-card rounded-lg p-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {copy.loading}
            </div>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="luxe-card rounded-lg border-destructive/25 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">{copy.errorTitle}</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{error}</p>
                <Button className="mt-4 h-9" onClick={() => void loadPlan(offset)}>
                  {messages.analytics.tryAgain}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && recommendations.length > 0 ? (
          <div className="space-y-3">
            {recommendations.map((item) => (
              <RecommendationCard key={item.productId} item={item} />
            ))}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {messages.analytics.pageSummary(currentPageNumber, totalPages, totalCount)}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={loading || offset === 0}
                  onClick={() => void loadPlan(Math.max(0, offset - PAGE_LIMIT))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {messages.analytics.previous}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={loading || !plan?.hasMore}
                  onClick={() => void loadPlan(plan?.nextOffset ?? offset)}
                >
                  {messages.analytics.next}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && recommendations.length === 0 ? (
          <div className="luxe-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-success/10 text-success ring-1 ring-success/15">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="inline-flex items-center rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                    {copy.inventoryGoodShape}
                  </div>
                  <h2 className="mt-2 text-xl font-semibold tracking-tight">{copy.noRestockNeeded}</h2>
                  <p className="mt-1 max-w-xl text-sm leading-5 text-muted-foreground">
                    {copy.noRestockDescription}
                  </p>
                </div>
              </div>
              <div className="pt-1 text-sm text-muted-foreground sm:text-right">
                {formatGeneratedAt(plan?.generatedAt, locale, copy)}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Metric
                label={copy.supplierLeadTime}
                value={`${plan?.leadTimeDays ?? 0} day${plan?.leadTimeDays === 1 ? "" : "s"}`}
              />
              <Metric
                label={copy.safetyStock}
                value={`${plan?.strategy?.safetyStockDays ?? 0} day${plan?.strategy?.safetyStockDays === 1 ? "" : "s"}`}
              />
            </div>
          </div>
        ) : null}
      </section>
    </PageContainer>
  );
}

function RecommendationCard({ item }) {
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.restockList;
  const style = urgencyStyles[item.urgency] ?? urgencyStyles.low;
  const labelMap = {
    high: copy.urgencyUrgent,
    medium: copy.urgencySoon,
    low: copy.urgencyWatch,
  };

  return (
    <article className="luxe-card rounded-lg p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted/70 text-muted-foreground">
            <Boxes className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold tracking-tight">{item.productName}</h3>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.reason}</p>
          </div>
        </div>
        <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${style.pill}`}>
          <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${style.dot}`} />
          {labelMap[item.urgency] || copy.urgencyWatch}
        </span>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <Metric label={copy.buyAtLeast} value={String(item.minimumSuggestedQuantity ?? 0)} strong />
        <Metric label={copy.estimatedCost} value={formatCurrencyLocal(item.estimatedCost, locale)} strong />
        <Metric label={copy.inStock} value={String(item.currentStock ?? 0)} />
        <Metric label={copy.leadTimeDemand} value={String(item.predictedLeadTimeDemand ?? 0)} />
        <Metric label={copy.sold30Days} value={String(item.soldLast30Days ?? 0)} />
        <Metric label={copy.dailySales} value={String(item.averageDailySales ?? 0)} />
      </div>
    </article>
  );
}

function Metric({ label, value, strong = false }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/60 px-3 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 font-mono text-sm tabular-nums ${strong ? "font-semibold text-foreground" : "font-medium text-foreground/85"}`}>
        {value}
      </div>
    </div>
  );
}
